import type { Theme } from '@mui/material'
import { alpha, MenuItem as MuiMenuItem, styled, Typography } from '@mui/material'
import { iconSizes } from '@ui/iconSizes'
import type { HTMLProps } from 'react'

export const StyledRootNavComponent = styled('div', {
  shouldForwardProp: (propName: PropertyKey) =>
    propName !== 'as' && !(['isActive', 'isExtendable'] as PropertyKey[]).includes(propName),
})<{
  isActive: boolean
  isExtendable: boolean
}>(({ theme, isActive, isExtendable }) => ({
  '&:hover, &:focus-visible, &:hover span': {
    color: theme.palette.primary.main,
    outline: 0,
  },
  ':not(:last-child)': {
    marginBottom: isExtendable ? 0 : theme.spacing(0.8),
  },
  borderRadius: 4,
  color: alpha(theme.palette.text.primary, 0.6),

  cursor: 'pointer',
  overflow: 'hidden',
  padding: theme.spacing(1.2, 2, 1.2, 2),
  transition: 'width 0.5s ease',

  width: '100%',

  ...(isActive
    ? {
        '&:hover, &:focus-visible': {
          color: theme.palette.primary.main,
        },
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }
    : {}),
}))

export const MenuItemBtn = styled('span')(({ theme }) => ({
  '& svg': {
    height: iconSizes.lg,
    transition: theme.transitions.duration.shortest + 'ms',
    width: iconSizes.lg,
  },
  '&:focus': {
    color: theme.palette.primary.main,
    outline: 0,
  },
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  gap: theme.spacing(1),
  margin: 0,
  padding: 0,
  width: '100%',
}))

const BaseLabel = (theme: Theme) => ({
  cursor: 'pointer',
  display: 'block',
  fontSize: '1.97rem',
  fontWeight: '500',
  opacity: 1,
  transition: theme.transitions.duration.shortest + 'ms',
})

export const Label = styled((props) => <Typography {...props} component="span" />, {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'shouldHideLabel',
})<HTMLProps<HTMLSpanElement>>(({ theme }) => ({
  ...BaseLabel(theme),
  flexGrow: 1,
  fontSize: '1.6rem',
  pointerEvents: 'none',
  textAlign: 'left',
  whiteSpace: 'nowrap',
}))

/**
 * Wraps the nav glyph so the shrunk-menu badge dot can anchor to it. The count
 * badge itself lives in `ExtendedLabel`, which collapses to `width: 0` when the
 * sidebar is shrunk — the dot is what survives that.
 */
export const IconSlot = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,
  position: 'relative',
})

export const BadgeDot = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  border: `1px solid ${theme.palette.background.default}`,
  borderRadius: '50%',
  height: theme.spacing(1.25),
  position: 'absolute',
  right: theme.spacing(-0.5),
  top: theme.spacing(-0.5),
  width: theme.spacing(1.25),
}))

export const ExtendedLabel = styled(Typography, {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'shouldHideLabel',
})<{
  shouldHideLabel: boolean
}>(({ shouldHideLabel }) => ({
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  overflow: 'hidden',
  width: shouldHideLabel ? 0 : 'auto',
}))

export const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  ...BaseLabel(theme),
  '&.Mui-selected': {
    background: 'unset !important',
  },
  background: 'none',
  border: 'none',
  color: theme.palette.text.primary,
  fontWeight: 500,
  lineHeight: 36 + 'px',
  textAlign: 'left',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  width: '100%',
}))

export const SubMenuListWrapper = styled('div', {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  display: 'grid',
  gridTemplateRows: isOpen ? '1fr' : '0fr',
  marginBottom: theme.spacing(0.8),
  transition: 'grid-template-rows 0.2s ease',
}))

export const SubMenuList = styled('div')(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 23,
  overflow: 'hidden',
  paddingLeft: theme.spacing(2),
}))
